/**
 * Pre-built MapLibre style expressions for clustering and color matching.
 *
 * Derived once at module load from PERIOD_COLORS / FOLKLORE_CATEGORIES /
 * MONUMENT_TYPE_COLORS so the map style objects in App.tsx can drop them
 * in verbatim. Includes per-period cluster aggregation (clusterProps) and
 * dominantPeriodColor (the let/case expression picking the most-frequent
 * period inside each cluster).
 *
 * Imported by App.tsx style builders; no runtime side effects.
 */
import { PERIOD_COLORS, FOLKLORE_CATEGORIES, MONUMENT_TYPE_COLORS } from '../constants/index.ts';

/**
 * MapLibre style expressions are recursive and accept literals, ["get", "x"]
 * sub-expressions, etc. The official type is heavy; this alias is the
 * minimal recursive shape we need for the builders below.
 */
export type MapExpression = string | number | boolean | null | MapExpression[];
type ExpressionArray = MapExpression[];

// --- Period color match expression ---
export const periodColorExpr: ExpressionArray = ["match", ["get", "period"]];
Object.entries(PERIOD_COLORS).forEach(([p, c]) => periodColorExpr.push(p, c));
periodColorExpr.push("#9b8ec4");

// --- Cluster properties: count each period per cluster ---
export const clusterProps: Record<string, ExpressionArray> = {};
export const periodKeys: { period: string; key: string; color: string }[] = [];
Object.entries(PERIOD_COLORS).forEach(([period, color]) => {
  const key = `c_${period.replace(/\s/g, "_")}`;
  clusterProps[key] = ["+", ["case", ["==", ["get", "period"], period], 1, 0]];
  periodKeys.push({ period, key, color });
});

export const maxExpr: ExpressionArray = ["max", ...periodKeys.map((p) => ["get", p.key] as ExpressionArray)];
const caseBranches: ExpressionArray = [];
periodKeys.forEach((p) => {
  caseBranches.push(["==", ["get", p.key], ["var", "mx"]], p.color);
});
export const dominantPeriodColor: ExpressionArray = ["let", "mx", maxExpr, ["case", ...caseBranches, "#6b7280"]];

// --- Cluster geometry, shared by all 19 clustered sources ---
// Rewritten 2026-08-19 after a recipient answered the outreach mail with
// «Svårt att läsa och få fram data», and a phone screenshot showed why. At
// country zoom every layer drew its own 36-64px fully opaque bubbles, and
// the land underneath disappeared. The radius now scales with zoom, so a
// cluster is a small density dot at country zoom and only grows once the
// map is close enough that its count means something. The before/after
// pair lives in screenshots/map-calm/, shot by scripts/map-calm-shot.mjs.
export const clusterRadiusExpr: ExpressionArray = [
  "interpolate", ["linear"], ["zoom"],
  4, ["step", ["get", "point_count"], 7, 25, 9, 100, 11, 750, 13],
  8, ["step", ["get", "point_count"], 11, 25, 14, 100, 17, 750, 20],
];

/** One shared cluster fill opacity. The old per-layer 0.62 to 1.0 values
 *  meant two stacked layers blended to solid paint; at 0.55 the basemap
 *  stays readable under an overlap. */
export const CLUSTER_OPACITY = 0.55;

// Folklore category color expression
export const folkloreCatColorExpr: ExpressionArray = ["match", ["get", "category"]];
FOLKLORE_CATEGORIES.forEach((c) => { if (c.id !== "all") folkloreCatColorExpr.push(c.id, c.color); });
folkloreCatColorExpr.push("#d4a843");

// Monument category color expression
export const monumentCatColorExpr: ExpressionArray = ["match", ["get", "monumentType"]];
Object.entries(MONUMENT_TYPE_COLORS).forEach(([id, color]) => monumentCatColorExpr.push(id, color));
monumentCatColorExpr.push("#2d8a56");
